/**
 * Centralized date formatting utility for consistent date display across the application
 */

/**
 * Formats a date to Month/Day/Year format with text month names
 * @param date - Date string, Date object, or null/undefined
 * @returns Formatted date string or 'N/A' for invalid dates
 */
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return 'N/A';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }

    const month = dateObj.toLocaleDateString('en-US', { month: 'long' });
    const day = dateObj.getDate();
    const year = dateObj.getFullYear();

    return `${month}/${day}/${year}`;
  } catch (error) {

    return 'N/A';
  }
};

/**
 * Formats a date with time to Month/Day/Year Hour:Minute format
 * @param date - Date string, Date object, or null/undefined
 * @returns Formatted date and time string or 'N/A' for invalid dates
 */
export const formatDateTime = (date: string | Date | null | undefined): string => {
  if (!date) return 'N/A';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }

    const month = dateObj.toLocaleDateString('en-US', { month: 'long' });
    const day = dateObj.getDate();
    const year = dateObj.getFullYear();
    const time = dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    return `${month}/${day}/${year} ${time}`;
  } catch (error) {

    return 'N/A';
  }
};

/**
 * Formats a date for input fields (YYYY-MM-DD format)
 * @param date - Date string, Date object, or null/undefined
 * @returns Formatted date string for input fields or empty string for invalid dates
 */
export const formatDateForInput = (date: string | Date | null | undefined): string => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    return dateObj.toISOString().split('T')[0];
  } catch (error) {

    return '';
  }
};

